/**
 * Troubleshooting flowchart for SmartBox outages — v2 (Jason 2026-06-02).
 *
 * v1 was a 41-node mermaid-derived flowchart. v2 strips it back to two
 * top-level paths with one starting question each; follow-up steps will
 * be added in subsequent slices.
 *
 *   Start (pick path)
 *     ├─ Remote — am I working remotely?  →  RemoteMqttCheck
 *     └─ On site — am I at the box?       →  OnSitePoweredCheck
 *
 * `role` colors the step header. `autoAnswerKey` lets autoAnswer.ts
 * inject a Box-derived suggestion (operator can override).
 *
 * Engine contract preserved: TroubleshootWizard.tsx imports `FLOWCHART`,
 * `START_NODE_ID`, and the node-kind types from this file — none of
 * those names or shapes have changed. Every `next` references an id
 * that exists in FLOWCHART.
 */

export type Role = "HQ" | "Maint" | "Joint" | "Neutral";

/** Auto-answer keys this flowchart understands. v2 has exactly one. */
export type AutoAnswerKey = "mqtt_publish_recent";

interface NodeBase {
  id: string;
  title: string;
  body: string;
  role: Role;
  /** Optional asset path for the step illustration. Placeholder for now. */
  image?: string;
}

export interface ActionNode extends NodeBase {
  kind: "action";
  /** Next step after the operator marks this action done. */
  next: string;
}

export interface DecisionNode extends NodeBase {
  kind: "decision";
  /** When non-null, autoAnswer.ts can pre-pick a branch from Box data. */
  autoAnswerKey?: AutoAnswerKey;
  /** Order is the order shown in the UI. */
  branches: { label: string; next: string }[];
}

export interface TerminalNode extends NodeBase {
  kind: "terminal";
}

export type Node = ActionNode | DecisionNode | TerminalNode;

/**
 * Step graph keyed by node id. Use `FLOWCHART[id]` to look up the next
 * step. Start at `Start`.
 */
export const FLOWCHART: Record<string, Node> = {
  Start: {
    id: "Start",
    kind: "decision",
    role: "Neutral",
    title: "Where are you troubleshooting from?",
    body:
      "Pick the path that matches where you are right now. We'll narrow the " +
      "steps based on that — remote-only checks for HQ work, physical " +
      "inspection for on-site work.",
    branches: [
      { label: "Remote — I'm at HQ", next: "RemoteMqttCheck" },
      { label: "On site — I'm at the box", next: "OnSitePoweredCheck" },
    ],
  },

  RemoteMqttCheck: {
    id: "RemoteMqttCheck",
    kind: "decision",
    role: "HQ",
    title:
      "Are recent MQTT messages making it from the edge device to " +
      "mqtt-azure-g5.tailc36a4e.ts.net?",
    body:
      "MTTR's publish-chain check (`box.publish_chain_broken`) fires when " +
      "the edge box is publishing locally (Tier 3 fresh) but central tiers " +
      "(broker Mongo + v2_camera_health / v2_mongo_scan) are silent past " +
      "the configured freshness window (MttrSettings.MqttPublishRecentMinutes). " +
      "Most common cause: wrong `MqttSettings.BrokerAddress` in the edge " +
      "box's `app_config`. The MTTR suggestion at the top of this branch " +
      "is derived from the live snapshot; override if you've already " +
      "verified differently.",
    autoAnswerKey: "mqtt_publish_recent",
    branches: [
      { label: "YES — messages are flowing", next: "End" },
      { label: "NO — publish chain looks broken", next: "End" },
    ],
  },

  OnSitePoweredCheck: {
    id: "OnSitePoweredCheck",
    kind: "decision",
    role: "Maint",
    title: "Is the SmartBox powered on?",
    body:
      "Look at the box. Check the front-panel power LED, listen for the " +
      "fan, and confirm at least one outlet on the wall plug is live " +
      "(plug a phone charger in if there's no other load).",
    branches: [
      { label: "YES — box is powered on", next: "End" },
      { label: "NO — box is dark", next: "End" },
    ],
  },

  End: {
    id: "End",
    kind: "terminal",
    role: "Neutral",
    title: "End of v1 troubleshooting path",
    body:
      "More steps will be added here in a future slice. For now, log your " +
      "finding on the ticket (the wizard already appended a [TS] note for " +
      "the answer you picked) and continue manually.",
  },
};

export const START_NODE_ID = "Start";
